/**
 * API Service Layer - Google Sheets Edition
 * Updated to ensure robust live data matching and production calculations.
 */
import { seedData } from './mockData';

const GAS_URL = import.meta.env.VITE_GAS_URL || '';
const USE_MOCK = !GAS_URL || GAS_URL.includes('your_id') || GAS_URL.includes('YOUR_COPIED_URL');

const getLocalDb = () => {
  const db = localStorage.getItem('bakery_db');
  if (!db) {
    localStorage.setItem('bakery_db', JSON.stringify(seedData));
    return seedData;
  }
  return JSON.parse(db);
};

const saveLocalDb = (data) => {
  localStorage.setItem('bakery_db', JSON.stringify(data));
};

const generateId = (prefix) => `${prefix}${Math.random().toString(36).substr(2, 9)}`;

/**
 * Robust Date Normalization
 * Handles ISO strings, Date objects, and YYYY-MM-DD from GAS or local inputs.
 */
const normalizeDate = (dateVal) => {
  if (!dateVal) return '';
  const str = String(dateVal);
  // Extract YYYY-MM-DD from ISO or standard date strings
  return str.split('T')[0].substring(0, 10);
};

const fetchFromBackend = async (action, payload = null) => {
  if (USE_MOCK) {
    const db = getLocalDb();
    switch (action) {
      case 'getProducts': return db.products;
      case 'saveProduct':
        const pIndex = db.products.findIndex(p => String(p.id) === String(payload.id));
        if (pIndex >= 0) db.products[pIndex] = payload;
        else db.products.push({ ...payload, id: payload.id || generateId('p') });
        saveLocalDb(db);
        return { success: true };
      case 'deleteProduct':
        db.products = db.products.filter(p => String(p.id) !== String(payload.id));
        db.customerProducts = db.customerProducts.filter(cp => String(cp.product_id) !== String(payload.id));
        saveLocalDb(db);
        return { success: true };
      case 'getRecipes': return db.recipes;
      case 'saveRecipe':
        const rIndex = db.recipes.findIndex(r => String(r.id) === String(payload.id));
        if (rIndex >= 0) db.recipes[rIndex] = payload;
        else db.recipes.push({ ...payload, id: payload.id || generateId('r') });
        saveLocalDb(db);
        return { success: true };
      case 'deleteRecipe':
        db.recipes = db.recipes.filter(r => String(r.id) !== String(payload.id));
        db.products = db.products.map(p => String(p.recipe_id) === String(payload.id) ? { ...p, recipe_id: '' } : p);
        saveLocalDb(db);
        return { success: true };
      case 'getCustomers': return db.customers;
      case 'saveCustomer':
        const cIndex = db.customers.findIndex(c => String(c.id) === String(payload.id));
        if (cIndex >= 0) db.customers[cIndex] = payload;
        else db.customers.push({ ...payload, id: payload.id || generateId('c') });
        saveLocalDb(db);
        return { success: true };
      case 'deleteCustomer':
        db.customers = db.customers.filter(c => String(c.id) !== String(payload.id));
        db.customerProducts = db.customerProducts.filter(cp => String(cp.customer_id) !== String(payload.id));
        saveLocalDb(db);
        return { success: true };
      case 'getCustomerProducts': return db.customerProducts;
      case 'saveCustomerProducts':
        db.customerProducts = db.customerProducts.filter(cp => String(cp.customer_id) === String(payload.customer_id));
        payload.product_ids.forEach(pid => {
          db.customerProducts.push({ customer_id: payload.customer_id, product_id: pid });
        });
        saveLocalDb(db);
        return { success: true };
      case 'getOrders': return db.orders;
      case 'getOrderLines': return db.orderLines;
      case 'saveOrder':
        const { order, lines } = payload;
        const oId = order.id || generateId('o');
        const oIdx = db.orders.findIndex(o => String(o.id) === String(oId));
        if (oIdx >= 0) db.orders[oIdx] = { ...order, id: oId };
        else db.orders.push({ ...order, id: oId });
        db.orderLines = db.orderLines.filter(ol => String(ol.order_id) !== String(oId));
        lines.forEach(l => db.orderLines.push({ ...l, order_id: oId, id: generateId('ol') }));
        saveLocalDb(db);
        return { success: true };
      default: return null;
    }
  }

  try {
    const response = await fetch(GAS_URL, {
      method: 'POST',
      body: JSON.stringify({ action, payload }),
      headers: { 'Content-Type': 'text/plain;charset=utf-8' }
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result.data;
  } catch (err) {
    console.error(`[API Error] ${action}:`, err);
    throw err;
  }
};

export const api = {
  getProducts: () => fetchFromBackend('getProducts'),
  saveProduct: (product) => fetchFromBackend('saveProduct', product),
  deleteProduct: (id) => fetchFromBackend('deleteProduct', { id }),
  getRecipes: () => fetchFromBackend('getRecipes'),
  saveRecipe: (recipe) => fetchFromBackend('saveRecipe', recipe),
  deleteRecipe: (id) => fetchFromBackend('deleteRecipe', { id }),
  getCustomers: () => fetchFromBackend('getCustomers'),
  saveCustomer: (customer) => fetchFromBackend('saveCustomer', customer),
  deleteCustomer: (id) => fetchFromBackend('deleteCustomer', { id }),
  getCustomerProducts: () => fetchFromBackend('getCustomerProducts'),
  saveCustomerProducts: (customerId, productIds) => fetchFromBackend('saveCustomerProducts', { customer_id: customerId, product_ids: productIds }),
  getOrders: () => fetchFromBackend('getOrders'),
  getOrderLines: () => fetchFromBackend('getOrderLines'),
  saveOrder: (order, lines) => fetchFromBackend('saveOrder', { order, lines }),

  /**
   * Universal Production Summary Calculation
   * Always calculates on the client side to ensure perfect matching with live Spreadsheet data.
   */
  getProductionSummary: async (date) => {
    // 1. Fetch raw data tables
    const [products, recipes, orders, lines] = await Promise.all([
      api.getProducts(),
      api.getRecipes(),
      api.getOrders(),
      api.getOrderLines()
    ]);

    const targetDateStr = normalizeDate(date);

    // 2. Filter Orders for the target delivery date
    const targetOrderIds = (orders || [])
      .filter(o => normalizeDate(o.delivery_date) === targetDateStr)
      .map(o => String(o.id));

    if (targetOrderIds.length === 0) {
      return { recipes: [], unlinkedProducts: [] };
    }

    // 3. Aggregate quantities from Order Lines
    const productTotals = {};
    (lines || []).forEach(l => {
      if (targetOrderIds.includes(String(l.order_id))) {
        const pid = String(l.product_id);
        productTotals[pid] = (productTotals[pid] || 0) + Number(l.quantity);
      }
    });

    // 4. Group by Recipe and calculate Dough Weights
    const recipeGroups = {};
    const unlinkedProducts = [];

    Object.entries(productTotals).forEach(([pid, qty]) => {
      const product = (products || []).find(p => String(p.id) === String(pid));
      if (!product) return;

      // Check if product is linked to a shared recipe
      if (product.recipe_id) {
        const recipe = (recipes || []).find(r => String(r.id) === String(product.recipe_id));
        if (recipe) {
          if (!recipeGroups[recipe.id]) {
            recipeGroups[recipe.id] = {
              recipe,
              products: [],
              totalDoughWeight: 0
            };
          }
          const individualWeight = Number(product.dough_weight) || 0;
          const lineWeight = qty * individualWeight;
          
          recipeGroups[recipe.id].products.push({
            product,
            qty,
            lineWeight
          });
          recipeGroups[recipe.id].totalDoughWeight += lineWeight;
        } else {
          unlinkedProducts.push({ product, qty });
        }
      } else {
        unlinkedProducts.push({ product, qty });
      }
    });

    // 5. Calculate Batches
    const finalRecipes = Object.values(recipeGroups).map(group => {
      const yieldAmt = Number(group.recipe.batch_yield) || 1;
      const mixerCap = Number(group.recipe.mixer_capacity) || 0;
      
      // Calculate suggested batches based on mixer capacity if available, otherwise on batch yield
      let batches = 1;
      if (mixerCap > 0 && group.totalDoughWeight > mixerCap) {
        batches = Math.ceil(group.totalDoughWeight / mixerCap);
      } else if (yieldAmt > 0) {
        batches = Math.max(1, Math.ceil(group.totalDoughWeight / yieldAmt));
      }

      return {
        ...group,
        batches
      };
    });

    return {
      recipes: finalRecipes,
      unlinkedProducts
    };
  }
};